import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Button, Card, useToast, Input, Label, Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@caruso/ui';
import { Loader2, QrCode, LogOut, RefreshCw, CheckCircle, XCircle, AlertCircle, Bot, Clock, Calendar, Save, Pencil } from 'lucide-react';
import QRCode from 'react-qr-code';
import whatsappService, { WhatsAppStatus, QRCodeResponse } from '@/services/whatsapp.service';
import { settingsService, WhatsAppAgentSettings } from '@/services/settings.service';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Dom' },
  { value: 1, label: 'Seg' },
  { value: 2, label: 'Ter' },
  { value: 3, label: 'Qua' },
  { value: 4, label: 'Qui' },
  { value: 5, label: 'Sex' },
  { value: 6, label: 'Sáb' },
];

export function WhatsAppConfig() {
  const [status, setStatus] = useState<WhatsAppStatus | null>(null);
  const [qrCode, setQrCode] = useState<QRCodeResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [checking, setChecking] = useState(true);
  const { toast } = useToast();

  // Agent settings state
  const [agentSettings, setAgentSettings] = useState<WhatsAppAgentSettings | null>(null);
  const [savingAgent, setSavingAgent] = useState(false);

  // Instance edit modal state
  const [isEditInstanceOpen, setIsEditInstanceOpen] = useState(false);
  const [editInstanceName, setEditInstanceName] = useState('');
  const [savingInstance, setSavingInstance] = useState(false);

  // Disconnect confirmation state
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);

  const checkStatus = useCallback(async () => {
    try {
      setChecking(true);
      const statusData = await whatsappService.getStatus();
      setStatus(statusData);

      if (!statusData.connection.connected && statusData.service.enabled) {
        const qrData = await whatsappService.getQRCode();
        if (qrData.qrcode) {
          setQrCode(qrData);
        }
      }
    } catch (error) {
      console.error('Erro ao verificar status:', error);
    } finally {
      setChecking(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConnect = async () => {
    try {
      setConnecting(true);
      const qrData = await whatsappService.connectInstance();

      // Log para debug
      console.log('QR Code Response:', qrData);

      if (qrData.qrcode || qrData.base64) {
        setQrCode(qrData);
        toast({
          title: 'QR Code Gerado',
          description: 'Escaneie o QR Code com seu WhatsApp',
        });
        startConnectionPolling();
      } else if (qrData.message) {
        toast({
          title: 'Aviso',
          description: qrData.message,
          variant: 'default',
        });
      } else {
        // Log se não veio nenhum QR Code
        console.error('No QR code in response:', qrData);
        toast({
          title: 'Erro',
          description: 'QR Code não foi gerado. Verifique os logs do console.',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Erro ao conectar:', error);
      console.error('Error response:', error.response?.data);
      toast({
        title: 'Erro',
        description: error.response?.data?.message || 'Não foi possível gerar o QR Code',
        variant: 'destructive',
      });
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      setLoading(true);
      const response = await whatsappService.logout();

      if (response.success) {
        setQrCode(null);
        toast({
          title: 'Desconectado',
          description: 'WhatsApp desconectado com sucesso',
        });
        await checkStatus();
      } else {
        toast({
          title: 'Erro',
          description: response.error || 'Erro ao desconectar',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Erro ao desconectar:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível desconectar',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
      setConfirmingDisconnect(false);
    }
  };

  const startConnectionPolling = () => {
    const interval = setInterval(async () => {
      const statusData = await whatsappService.getStatus();

      if (statusData.connection.connected) {
        setStatus(statusData);
        setQrCode(null);
        clearInterval(interval);

        toast({
          title: 'Conectado!',
          description: `WhatsApp conectado: ${statusData.connection.profileName}`,
        });
      }
    }, 3000);

    setTimeout(() => clearInterval(interval), 120000);
  };

  const loadAgentSettings = useCallback(async () => {
    try {
      const settings = await settingsService.getWhatsAppAgentSettings();
      setAgentSettings(settings);
    } catch (error) {
      console.error('Erro ao carregar configurações do agente:', error);
    }
  }, []);

  const handleSaveAgentSettings = async () => {
    if (!agentSettings) return;

    try {
      setSavingAgent(true);
      await settingsService.updateWhatsAppAgentSettings(agentSettings);
      toast({
        title: 'Configurações salvas',
        description: 'As configurações do agente foram atualizadas com sucesso',
      });
    } catch (error) {
      console.error('Erro ao salvar configurações do agente:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível salvar as configurações do agente',
        variant: 'destructive',
      });
    } finally {
      setSavingAgent(false);
    }
  };

  const toggleDay = (day: number) => {
    if (!agentSettings) return;
    const newDays = agentSettings.daysOfWeek.includes(day)
      ? agentSettings.daysOfWeek.filter(d => d !== day)
      : [...agentSettings.daysOfWeek, day].sort((a, b) => a - b);
    setAgentSettings({ ...agentSettings, daysOfWeek: newDays });
  };

  const handleOpenEditInstance = () => {
    setEditInstanceName(status?.service.instance || '');
    setIsEditInstanceOpen(true);
  };

  const handleSaveInstance = async () => {
    if (!editInstanceName.trim()) {
      toast({
        title: 'Erro',
        description: 'O nome da instância não pode estar vazio',
        variant: 'destructive',
      });
      return;
    }

    try {
      setSavingInstance(true);
      await settingsService.updateIntegrations({
        whatsapp: {
          evolutionInstance: editInstanceName.trim(),
        },
      });
      toast({
        title: 'Instância atualizada',
        description: 'O nome da instância foi atualizado com sucesso',
      });
      setIsEditInstanceOpen(false);
      // Recarrega o status para mostrar o novo nome
      await checkStatus();
    } catch (error) {
      console.error('Erro ao salvar instância:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível atualizar a instância',
        variant: 'destructive',
      });
    } finally {
      setSavingInstance(false);
    }
  };

  useEffect(() => {
    checkStatus();
    loadAgentSettings();
  }, [checkStatus, loadAgentSettings]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!connecting) {
        checkStatus();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [connecting, checkStatus]);

  if (checking) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-brand-gold" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-serif text-brand-gold">Configuração do WhatsApp</h1>
          <p className="text-brand-ivory/60 mt-1">
            Configure e gerencie sua conexão com o WhatsApp Business
          </p>
        </div>
        <Button onClick={checkStatus} variant="outline" disabled={loading}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Atualizar Status
        </Button>
      </div>

      {/* Status do Serviço */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Card className="bg-brand-ink/50 border-brand-gold/20">
          <div className="p-6 space-y-4">
            <h3 className="text-lg font-serif text-brand-gold">Status do Serviço</h3>
            <p className="text-sm text-brand-ivory/60">
              Informações sobre o serviço de WhatsApp
            </p>

            {status?.service.enabled ? (
              <div className="space-y-3 pt-2">
                <div className="flex items-center justify-between py-2 border-b border-brand-gold/10">
                  <span className="text-sm font-medium text-brand-ivory/80">Serviço</span>
                  <div className="flex items-center gap-2 text-green-400">
                    <CheckCircle className="h-4 w-4" />
                    <span className="text-sm">Habilitado</span>
                  </div>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-brand-gold/10">
                  <span className="text-sm font-medium text-brand-ivory/80">Provedor</span>
                  <span className="text-sm text-brand-ivory/60">{status.service.provider}</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm font-medium text-brand-ivory/80">Instância</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-brand-ivory/60">{status.service.instance}</span>
                    <button
                      type="button"
                      onClick={handleOpenEditInstance}
                      className="p-1 text-brand-ivory/40 hover:text-brand-gold transition-colors"
                      title="Editar nome da instância"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-yellow-900/20 border border-yellow-500/30 p-4 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-yellow-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-yellow-400">Serviço Desabilitado</p>
                    <p className="text-sm text-yellow-400/70 mt-1">
                      O serviço de WhatsApp não está configurado. Configure as variáveis de ambiente:
                      EVOLUTION_API_KEY e EVOLUTION_API_URL
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>
      </motion.div>

      {/* Status da Conexão */}
      {status?.service.enabled && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="bg-brand-ink/50 border-brand-gold/20">
            <div className="p-6 space-y-4">
              <h3 className="text-lg font-serif text-brand-gold">Status da Conexão</h3>
              <p className="text-sm text-brand-ivory/60">
                {status.connection.connected
                  ? 'WhatsApp conectado e pronto para uso'
                  : 'Configure a conexão com WhatsApp'}
              </p>

              {status.connection.connected ? (
                <div className="space-y-3 pt-2">
                  <div className="flex items-center justify-between py-2 border-b border-brand-gold/10">
                    <span className="text-sm font-medium text-brand-ivory/80">Status</span>
                    <div className="flex items-center gap-2 text-green-400">
                      <CheckCircle className="h-4 w-4" />
                      <span className="text-sm">Conectado</span>
                    </div>
                  </div>
                  {status.connection.profileName && (
                    <div className="flex items-center justify-between py-2 border-b border-brand-gold/10">
                      <span className="text-sm font-medium text-brand-ivory/80">Nome</span>
                      <span className="text-sm text-brand-ivory/60">{status.connection.profileName}</span>
                    </div>
                  )}
                  {status.connection.phoneNumber && (
                    <div className="flex items-center justify-between py-2 border-b border-brand-gold/10">
                      <span className="text-sm font-medium text-brand-ivory/80">Número</span>
                      <span className="text-sm text-brand-ivory/60">{status.connection.phoneNumber}</span>
                    </div>
                  )}
                  <div className="pt-4">
                    {confirmingDisconnect ? (
                      <div className="space-y-3">
                        <p className="text-sm text-brand-ivory/80 text-center">
                          Tem certeza que deseja desconectar o WhatsApp?
                        </p>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => setConfirmingDisconnect(false)}
                            variant="outline"
                            disabled={loading}
                            className="flex-1"
                          >
                            Cancelar
                          </Button>
                          <Button
                            onClick={handleDisconnect}
                            variant="destructive"
                            disabled={loading}
                            className="flex-1"
                          >
                            {loading ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <LogOut className="h-4 w-4 mr-2" />
                            )}
                            Sim, desconectar
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button
                        onClick={() => setConfirmingDisconnect(true)}
                        variant="destructive"
                        disabled={loading}
                        className="w-full"
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        Desconectar
                      </Button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-4 pt-2">
                  <div className="flex items-center justify-between py-2 border-b border-brand-gold/10">
                    <span className="text-sm font-medium text-brand-ivory/80">Status</span>
                    <div className="flex items-center gap-2 text-red-400">
                      <XCircle className="h-4 w-4" />
                      <span className="text-sm">Desconectado</span>
                    </div>
                  </div>

                  {/* QR Code */}
                  {(qrCode?.qrcode || qrCode?.base64) && (
                    <div className="flex flex-col items-center space-y-4 py-6">
                      <div className="p-4 bg-white rounded-lg border border-brand-gold/20">
                        {qrCode.base64 ? (
                          <img src={qrCode.base64} alt="QR Code" className="w-64 h-64" />
                        ) : (
                          <QRCode value={qrCode.qrcode!} size={256} />
                        )}
                      </div>
                      <div className="text-center space-y-1">
                        <p className="text-sm text-brand-ivory/60">
                          Abra o WhatsApp no seu celular
                        </p>
                        <p className="text-sm text-brand-ivory/60">
                          Vá em Configurações &gt; Dispositivos conectados
                        </p>
                        <p className="text-sm text-brand-ivory/60">
                          Toque em "Conectar dispositivo" e escaneie o código
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Botões de Ação */}
                  <div className="flex gap-2">
                    {!(qrCode?.qrcode || qrCode?.base64) && (
                      <Button
                        onClick={handleConnect}
                        disabled={connecting || loading}
                        variant="premium"
                        className="flex-1"
                      >
                        {connecting ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <QrCode className="h-4 w-4 mr-2" />
                        )}
                        Gerar QR Code
                      </Button>
                    )}

                    {(qrCode?.qrcode || qrCode?.base64) && (
                      <Button
                        onClick={handleConnect}
                        variant="outline"
                        disabled={connecting || loading}
                        className="flex-1"
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Novo QR Code
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Card>
        </motion.div>
      )}

      {/* Configurações do Agente */}
      {status?.service.enabled && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <Card className="bg-brand-ink/50 border-brand-gold/20">
            <div className="p-6 space-y-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-brand-gold/10 rounded-lg">
                  <Bot className="h-5 w-5 text-brand-gold" />
                </div>
                <div>
                  <h3 className="text-lg font-serif text-brand-gold">Configurações do Agente</h3>
                  <p className="text-sm text-brand-ivory/60">
                    Configure quando os fluxos automáticos podem ser disparados
                  </p>
                </div>
              </div>

              {agentSettings && (
                <div className="space-y-6">
                  {/* Ativar/Desativar Agente */}
                  <div className="flex items-center justify-between py-3 border-b border-brand-gold/10">
                    <div className="space-y-0.5">
                      <Label className="text-sm font-medium text-brand-ivory/80">
                        Agente Ativo
                      </Label>
                      <p className="text-xs text-brand-ivory/50">
                        Ativa ou desativa todos os fluxos automáticos
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={agentSettings.enabled}
                      onClick={() => setAgentSettings({ ...agentSettings, enabled: !agentSettings.enabled })}
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-gold focus:ring-offset-2 focus:ring-offset-brand-ink ${
                        agentSettings.enabled ? 'bg-brand-gold' : 'bg-brand-smoke/50'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          agentSettings.enabled ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Horário de Funcionamento */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-brand-ivory/80">
                      <Clock className="h-4 w-4" />
                      <span className="text-sm font-medium">Horário de Funcionamento</span>
                      <span className="text-xs text-brand-ivory/40 ml-auto">(Fuso: São Paulo)</span>
                    </div>
                    <p className="text-xs text-brand-ivory/50">
                      Período em que o agente pode enviar mensagens automaticamente
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs text-brand-ivory/60">Início</Label>
                        <Input
                          type="time"
                          value={agentSettings.startTime}
                          onChange={(e) =>
                            setAgentSettings({ ...agentSettings, startTime: e.target.value })
                          }
                          className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs text-brand-ivory/60">Fim</Label>
                        <Input
                          type="time"
                          value={agentSettings.endTime}
                          onChange={(e) =>
                            setAgentSettings({ ...agentSettings, endTime: e.target.value })
                          }
                          className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Dias da Semana */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-brand-ivory/80">
                      <Calendar className="h-4 w-4" />
                      <span className="text-sm font-medium">Dias da Semana</span>
                    </div>
                    <p className="text-xs text-brand-ivory/50">
                      Selecione os dias em que o agente pode operar
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {DAYS_OF_WEEK.map((day) => (
                        <button
                          key={day.value}
                          type="button"
                          onClick={() => toggleDay(day.value)}
                          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                            agentSettings.daysOfWeek.includes(day.value)
                              ? 'bg-brand-gold text-brand-ink'
                              : 'bg-brand-smoke/50 text-brand-ivory/60 hover:bg-brand-smoke/70'
                          }`}
                        >
                          {day.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Botão Salvar */}
                  <Button
                    onClick={handleSaveAgentSettings}
                    disabled={savingAgent}
                    variant="premium"
                    className="w-full"
                  >
                    {savingAgent ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Salvar Configurações
                  </Button>
                </div>
              )}

              {!agentSettings && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-brand-gold/50" />
                </div>
              )}
            </div>
          </Card>
        </motion.div>
      )}

      {/* Instruções */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card className="bg-brand-ink/50 border-brand-gold/20">
          <div className="p-6 space-y-4">
            <h3 className="text-lg font-serif text-brand-gold">Como Configurar</h3>
            <ol className="list-decimal list-inside space-y-2 text-sm text-brand-ivory/60">
              <li>Certifique-se de que o serviço está habilitado (configure as variáveis de ambiente)</li>
              <li>Clique em "Gerar QR Code" para iniciar a conexão</li>
              <li>Abra o WhatsApp no seu celular</li>
              <li>Vá em Configurações &gt; Dispositivos conectados</li>
              <li>Toque em "Conectar dispositivo"</li>
              <li>Escaneie o QR Code exibido na tela</li>
              <li>Aguarde a confirmação da conexão</li>
            </ol>
          </div>
        </Card>
      </motion.div>

      {/* Modal de Edição da Instância */}
      <Dialog open={isEditInstanceOpen} onOpenChange={setIsEditInstanceOpen}>
        <DialogContent className="bg-brand-ink border-brand-gold/20">
          <DialogHeader>
            <DialogTitle className="text-brand-gold">Editar Nome da Instância</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-sm text-brand-ivory/80">Nome da Instância</Label>
              <Input
                value={editInstanceName}
                onChange={(e) => setEditInstanceName(e.target.value)}
                placeholder="Digite o nome da instância"
                className="bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
              />
              <p className="text-xs text-brand-ivory/50">
                Este nome identifica sua conexão com o WhatsApp
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsEditInstanceOpen(false)}
              disabled={savingInstance}
            >
              Cancelar
            </Button>
            <Button
              variant="premium"
              onClick={handleSaveInstance}
              disabled={savingInstance}
            >
              {savingInstance ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
